/**
 * v2.71.96 — useNotifications() — single source of truth for the bell.
 *
 * What it does
 * ────────────
 *   • Fetches the current user's notifications + unread count via REST
 *     when the hook mounts.
 *   • Opens a WebSocket to `/api/notifications/ws` (auto-reconnects with
 *     exponential backoff). New notifications pushed by the backend are
 *     merged into the in-memory list and bump the badge instantly.
 *   • Exposes `markRead`, `markAllRead`, `delete`, `clearAll` actions.
 *   • Falls back to a 60 s polling loop if the WS handshake fails
 *     repeatedly (e.g. behind some corporate proxies that strip WS).
 *
 * Auth
 * ────
 *   REST: axios is already configured with `withCredentials:true` AND
 *   any Bearer token in localStorage — no extra wiring here.
 *   WS:   the backend resolver accepts `?token=` (Bearer) for non-cookie
 *         users (client/dealer/builder) OR the `he_session` HttpOnly
 *         cookie for admin/team.
 *
 * Strictly isolated — never touches WhatsApp / PropertyDetail / Office
 * shortlist storage. Lives entirely in `localStorage.auth_token` + the
 * `notifications` REST routes.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import api, { BACKEND_URL } from "./api";
import { emitDataChange } from "./dataSync";
import { useAuth } from "./AuthContext";

const RECONNECT_MIN_MS = 1500;
const RECONNECT_MAX_MS = 30000;
const POLL_FALLBACK_MS = 60000;

function readToken() {
  try { return localStorage.getItem("auth_token") || ""; } catch { return ""; }
}

function wsUrl() {
  const tok = readToken();
  const base = (BACKEND_URL || window.location.origin).replace(/^http/, "ws");
  const q = tok ? `?token=${encodeURIComponent(tok)}` : "";
  return `${base}/api/notifications/ws${q}`;
}

export function useNotifications({ enabled = true } = {}) {
  const { user } = useAuth();
  const isCustomerRole = user && ["client", "dealer", "builder", "admin"].includes(user.role);
  const live = Boolean(enabled && user && isCustomerRole);

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectDelay = useRef(RECONNECT_MIN_MS);
  const pollTimer = useRef(null);

  /* ─── REST helpers ───────────────────────────────────────────────── */
  const refresh = useCallback(async () => {
    if (!live) return;
    setLoading(true);
    try {
      const [list, count] = await Promise.all([
        api.get("/notifications?limit=50"),
        api.get("/notifications/count"),
      ]);
      setItems(list.data?.results || []);
      setTotal(list.data?.total ?? 0);
      setUnread(count.data?.unread ?? 0);
    } catch (_) { /* network — keep last state */ }
    finally { setLoading(false); }
  }, [live]);

  const refreshCountOnly = useCallback(async () => {
    if (!live) return;
    try {
      const r = await api.get("/notifications/count");
      setUnread(r.data?.unread ?? 0);
      setTotal(r.data?.total ?? 0);
    } catch (_) { /* noop */ }
  }, [live]);

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_status: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${id}/read`); } catch (_) { refreshCountOnly(); }
  }, [refreshCountOnly]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read_status: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch (_) { refreshCountOnly(); }
  }, [refreshCountOnly]);

  const remove = useCallback(async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    setUnread((u) => {
      const n = items.find((x) => x.id === id);
      return n && !n.read_status ? Math.max(0, u - 1) : u;
    });
    setTotal((t) => Math.max(0, t - 1));
    try { await api.delete(`/notifications/${id}`); } catch (_) { refreshCountOnly(); }
  }, [items, refreshCountOnly]);

  const clearAll = useCallback(async () => {
    setItems([]); setUnread(0); setTotal(0);
    try { await api.delete("/notifications"); } catch (_) { refreshCountOnly(); }
  }, [refreshCountOnly]);

  /* ─── WebSocket ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (!live) return undefined;

    let cancelled = false;

    const startPollFallback = () => {
      if (pollTimer.current) return;
      pollTimer.current = setInterval(refreshCountOnly, POLL_FALLBACK_MS);
    };
    const stopPollFallback = () => {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    };

    const connect = () => {
      if (cancelled) return;
      let ws;
      try { ws = new WebSocket(wsUrl()); }
      catch (_) { startPollFallback(); return; }
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) { ws.close(); return; }
        setConnected(true);
        reconnectDelay.current = RECONNECT_MIN_MS;
        stopPollFallback();
      };
      ws.onmessage = (e) => {
        let msg = null;
        try { msg = JSON.parse(e.data); } catch (_) { /* could be "pong" plain */ }
        if (!msg) return;
        if (msg.type === "ping") {
          try { ws.send("ping"); } catch (_) { /* noop */ }
          return;
        }
        if (msg.type === "notification.new" && msg.data) {
          setItems((prev) => [msg.data, ...prev].slice(0, 50));
          setUnread((u) => u + 1);
          setTotal((t) => t + 1);
          // v2.72 — Cross-client real-time sync piggyback: a pushed
          // notification means something changed server-side (new submission,
          // new lead, …). Nudge the data-sync bus so any mounted list /
          // dashboard refetches instantly — no manual refresh needed.
          const NOTIF_DOMAINS = {
            property: ["properties", "approvals", "dashboard"],
            lead: ["leads", "enquiries", "dashboard"],
            visit: ["leads", "dashboard"],
            project_inquiry: ["projects", "leads", "enquiries", "dashboard"],
            system: ["dashboard"],
          };
          emitDataChange(NOTIF_DOMAINS[msg.data.notification_type] || ["dashboard"]);
        }
        // v2.79 Phase 2 — Dedicated data.sync channel. Unlike
        // notification.new this does NOT touch the bell badge — it
        // exists purely to invalidate dataSync domains so mounted
        // lists / detail pages refresh in real time across every
        // logged-in session (no 45 s polling wait).
        if (msg.type === "data.sync" && msg.data) {
          const { domains, payload } = msg.data;
          if (Array.isArray(domains) && domains.length > 0) {
            emitDataChange(domains, payload || null);
          }
        }
      };
      ws.onerror = () => { /* close handler reconnects */ };
      ws.onclose = (ev) => {
        setConnected(false);
        wsRef.current = null;
        if (cancelled) return;
        // 4401 = auth failed; don't loop forever
        if (ev.code === 4401) { startPollFallback(); return; }
        const delay = reconnectDelay.current;
        reconnectDelay.current = Math.min(RECONNECT_MAX_MS, delay * 2);
        reconnectTimer.current = setTimeout(connect, delay);
      };
    };

    refresh();
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
      stopPollFallback();
      try { wsRef.current?.close(); } catch (_) { /* noop */ }
      wsRef.current = null;
    };
  }, [live, refresh, refreshCountOnly]);

  return {
    items, unread, total, loading, connected,
    refresh, markRead, markAllRead, remove, clearAll,
  };
}
